<template>
  <div class="forgot-uid">
    <v-container class="forgot-uid__container">
      <v-row>
        <v-col v-if="$vuetify.breakpoint.mdAndUp">
          <sc-placeholder name="leftcontent" :rendering="rendering" />
        </v-col>
        <v-col>
          <v-card outlined class="forgot-uid__form-container">
            <div class="forgot-uid__form-header">
              <img src="/img/log_in_to_investor.svg" alt="" />
            </div>
            <v-container v-if="!reminderSent" class="forgot-uid__form-inner-container">
              <v-form ref="form" v-model="valid" lazy-validation class="forgot-uid__form-content">
                <v-row class="forgot-uid__row">
                  <v-col cols="12">
                    <h3 class="forgot-uid__form-subtitle">
                      <sc-text :field="fields.FormSubtitle" />
                    </h3>
                    <p class="forgot-uid__form-description">
                      <sc-text :field="fields.FormDescription" />
                    </p>
                  </v-col>
                </v-row>
                <v-row class="forgot-uid__row">
                  <v-col cols="12">
                    <div class="forgot-uid__error" v-if="error">
                      {{ error }}
                    </div>
                    <div class="forgot-uid__label">
                      <sc-text :field="fields.EmailLabel" />
                    </div>
                    <v-text-field
                      :rules="emailValidationRules"
                      v-model="email"
                      @blur="sanitiseEmail"
                      validate-on-blur
                      flat
                      outlined
                      solo
                      class="forgot-uid__input email-input"
                      maxlength="80"
                    />
                  </v-col>
                </v-row>
                <v-row class="forgot-uid__row">
                  <v-col cols="4" class="forgot-uid__short-column">
                    <div class="forgot-uid__label">
                      <sc-text :field="fields.DialCodeLabel" />
                    </div>
                    <v-autocomplete
                      flat
                      outlined
                      solo
                      :items="autocompleteValues"
                      v-model="countryCode"
                      :rules="countryCodeValidationRules"
                    >
                      <template v-slot:selection>
                        {{ countryCode }}
                      </template>
                    </v-autocomplete>
                  </v-col>
                  <v-col cols="8" class="forgot-uid__long-column">
                    <div class="forgot-uid__label">
                      <sc-text :field="fields.PhoneLabel" />
                    </div>
                    <v-text-field
                      :rules="mobileValidationRules"
                      v-model="mobile"
                      @blur="sanitiseMobile"
                      flat
                      outlined
                      solo
                      class="forgot-uid__input mobile-input"
                    />
                  </v-col>
                </v-row>

                <v-row class="forgot-uid__row forgot-uid__row--buttons">
                  <v-col align-self="end" cols="12">
                    <router-link to="/login" class="forgot-uid__button-link">
                      {{ fields.CancelButtonLabel.value }}
                    </router-link>
                    <v-btn
                      class="forgot-uid__primary-action"
                      color="primary"
                      large
                      tile
                      elevation="0"
                      :disabled="!valid || responsePending"
                      @click="submitUserIdReminderForm"
                      :loading="responsePending"
                    >
                      {{ fields.SubmitButtonLabel.value }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-form>
            </v-container>
            <v-container v-else class="forgot-uid__form-inner-container">
              <v-row>
                <v-col cols="12" class="text-center forgot-uid__content">
                  <h3 class="forgot-uid__form-subtitle" v-if="fields.ReminderSentTitle">
                    <sc-text :field="fields.ReminderSentTitle" />
                  </h3>
                  <sc-image :media="fields.ReminderSentImage"  class="forgot-uid__success-mark" />
                  <v-expand-transition>
                    <div v-show="reminderSent">
                      <sc-rich-text
                        :field="fields.ReminderSent"
                        class="forgot-uid__success-message"
                      />
                      <router-link to="/login">
                        <v-btn
                          class="forgot-uid__primary-action"
                          color="primary"
                          large
                          tile
                          elevation="0"
                          :block="$vuetify.breakpoint.xsOnly"
                        >
                          {{ fields.FinishedButtonLabel.value }}
                        </v-btn>
                      </router-link>
                    </div>
                  </v-expand-transition>
                </v-col>
              </v-row>
            </v-container>
          </v-card>
          <sc-placeholder name="helpsection" class="help-card--forgotuid" :rendering="rendering" />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
import {
  Text,
  Image,
  isExperienceEditorActive,
  Placeholder,
  RichText,
} from '@sitecore-jss/sitecore-jss-vue';
import countryCodes from '../../lib/countryCodes';
import { requestUserIdReminder } from '../../api/index';
import challengerEnv from '../../lib/challengerEnv';
import { validation, sanitisation } from '../../lib/validation';

export default {
  name: 'ForgotUserId',
  components: {
    ScText: Text,
    ScImage : Image,
    ScRichText: RichText,
    ScPlaceholder: Placeholder,
  },
  data: function () {
    return {
      valid: false,
      responsePending: false,
      reminderSent: false,
      error: '',
      countryCodes: countryCodes,
      countryCode: '+61',
      countryCodeValidationRules: [
        (v) => validation.valueRequired(v) || this.fields.CountryCodeRequired.value,
      ],
      mobile: '',
      mobileValidationRules: [
        (v) => validation.valueRequired(v) || this.fields.MobileRequired.value,
        (v) => validation.mobileLength(v) || this.fields.MobileTooLong.value,
        (v) => validation.mobileNumericLength(v) || this.fields.MobileTooManyDigits.value,
        (v) => validation.anyNumerals(v) || this.fields.MobileInvalidFormat.value,
      ],
      email: '',
      emailValidationRules: [
        (v) => validation.valueRequired(v) || this.fields.EmailRequired.value,
        (v) => validation.emailLength(v) || this.fields.EmailTooLong.value,
        (v) => validation.emailFormat(v) || this.fields.EmailInvalidFormat.value,
      ],
    };
  },
  computed: {
    autocompleteValues() {
      if (!this.countryCodes) {
        return [];
      }

      return this.countryCodes.map((item) => ({
        text: `${item.name} (${item.dialCode})`,
        value: item.dialCode,
      }));
    },
    isSitecoreExperienceEditorOn() {
      // TODO may need to add other check like the preview mode
      return isExperienceEditorActive();
    },
  },
  props: {
    fields: {
      type: Object,
      default: () => ({
        HelpCardTitle: {
          value: 'Help Title',
        },
        HelpCardDescription: {
          value: 'Help Content',
        },
        SubmitButtonLabel: {
          value: 'Submit',
        },
        CancelButtonLabel: {
          value: 'Cancel',
        },
        FinishedButtonLabel: {
          value: 'Finished',
        },
      }),
    },
    rendering: {
      type: Object,
      default: () => ({}),
    },
  },
  methods: {
    submitUserIdReminderForm: function (event) {
      this.valid = this.$refs.form.validate();
      if (this.valid) {
        this.error = '';
        this.responsePending = true;
        requestUserIdReminder(this.email, this.countryCode + this.mobile)
          .then(
            (successResponse) => {
              this.reminderSent = successResponse.success;
              if (!this.responseSuccess) {
                // generic error until API returns specific error codes
                this.error =
                  'Your request cannot be processed.  Please try again.  Alternatively contact our Investor Services team on 13 35 66 for assistance.';
              }
            },
            (failureResponse) => {
              if(this.$logger && typeof(this.$logger)!=='undefined'){
                this.$logger.warning('failure: %o', failureResponse);
              }
              // generic error until API returns specific error codes
              this.error =
                'Your request cannot be processed.  Please try again.  Alternatively contact our Investor Services team on 13 35 66 for assistance.';
            }
          )
          .finally(() => {
            this.responsePending = false;
          });
      }
    },
    sanitiseMobile: function (event) {
      this.mobile = sanitisation.removeLeadingZeros(sanitisation.removeNonNumeric(this.mobile));
    },
    sanitiseEmail: function (event) {
      this.email = sanitisation.trim(this.email);
    },
  },
};
</script>

<style lang="scss" scoped>
.forgot-uid {
  background-color: map-deep-get($colors, 'white-off', 'base');
  min-height: 80vh;

  &__container {
    max-width: 1185px;
    padding: 12px 0 0;

    .row {
      margin: 0 !important;
    }

    @media #{map-get($display-breakpoints, 'sm-only')} {
      padding: 57px 96px 0;

      .col {
        padding: 0;
      }
    }

    @media #{map-get($display-breakpoints, 'xs-only')} {
      padding: 0;

      .col {
        padding: 0;
      }
    }
  }

  &__form-header {
    margin-left: -22px;
  }

  &__form-container {
    border-radius: 0 !important;
    margin: 0;
    padding: 30px 42px;

    @media #{map-get($display-breakpoints, 'xs-only')} {
      border: 0 !important;
      padding: 15px;
    }
  }

  &__form-inner-container {
    //@media #{map-get($display-breakpoints, 'xs-only')} {
    padding: 0;
    //}
  }

  &__label {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: map-deep-get($colors, 'grey-cardcontent', 'base');
  }

  &__row {
    flex-wrap: nowrap;

    @media #{map-get($display-breakpoints, 'sm-only')} {
      margin: 0;
    }
  }

  &__row .col {
    padding: 0px;
  }

  &__short-column.col {
    padding-right: 12px;
    min-width: 100px;
  }

  &__long-column.col {
    flex-shrink: 1;
  }

  &__form-subtitle {
    font-size: 16px;
    margin-bottom: 12px;
    text-align: left;
  }

  &__primary-action {
    @extend .btn-primary;
    float: right;
  }

  &__secondary-action {
    text-transform: none;
  }

  &__button-link {
    display: inline-flex;
    height: 44px;
    text-decoration: none;
    align-items: center;

    &:hover {
      text-decoration: underline;
    }
  }

  &__content {
    padding: 0;
  }

  &__success-message {
    text-align: left;
  }

  &__error {
    color: map-deep-get($colors, 'danger', 'base');
    margin-bottom: 12px;
  }

  &__success-mark {
    height: 80px;
    margin: 24px 0 32px;
  }
}
.help-card--forgotuid{
  margin: 0px;
  @media #{map-get($display-breakpoints, 'xs-only')} {
    margin: 15px;
  }
}
</style>
<style lang="scss">
.forgot-uid__input fieldset {
  border-color: currentColor !important; // Fix Safari bug causing multi-color borders
}
</style>
